import { useCallback, useRef, useState } from 'react';

import { useEventListener } from '..';
import {
  CloseReason,
  ToggleOnCloseHandlerOptions,
  UseToggleWithNavigation,
} from './types';

const addHistoryEntry = () => {
  window.history.pushState({}, '', null);
};

const navigateBackAndPopstate = () => {
  window.history.back();
};

const useToggleWithNavigation: UseToggleWithNavigation = ({
  onOpen,
  onClose,
} = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeReason = useRef<CloseReason>('navigate-back');

  const handleClose = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      const closeOpts: ToggleOnCloseHandlerOptions = {
        reason: closeReason.current,
      };
      if (onClose) onClose(closeOpts);
    }
  }, [isOpen, setIsOpen, onClose]);

  useEventListener('popstate', handleClose);

  const openWithNavigation = useCallback(() => {
    closeReason.current = 'navigate-back';
    setIsOpen((currentIsOpen) => {
      if (!currentIsOpen) {
        addHistoryEntry();
        if (onOpen) onOpen();
      }
      return true;
    });
  }, [setIsOpen, onOpen]);

  const closeWithNavigation = useCallback(() => {
    if (isOpen) {
      closeReason.current = 'close';
      navigateBackAndPopstate();
    }
  }, [isOpen]);

  const toggleWithNavigation = useCallback(() => {
    if (isOpen) {
      closeWithNavigation();
    } else {
      openWithNavigation();
    }
  }, [isOpen, closeWithNavigation, openWithNavigation]);

  return {
    isOpen,
    open: openWithNavigation,
    close: closeWithNavigation,
    toggle: toggleWithNavigation,
  };
};

export default useToggleWithNavigation;
